/**
 * `/onboarding` — first-run wizard for new accounts.
 *
 * Phase 14 Stream C, with the W1-I G-EMPTY2 first-run rework. Three
 * steps — the third was originally a one-shot "open your first cycle"
 * form which produced a useless first roster (no stations, no on-call
 * types, no tiers configured yet means the solver had nothing to chew
 * on). Replaced with a "Configure your team" step that hands the lead
 * off to the Setup tree (`/teams/:teamId/setup/sessions`) so the cycle
 * they open later actually has a team shape to schedule against. The
 * lead can still skip and reach the empty-config callout on TeamPage
 * (G-EMPTY1) if they prefer to come back later.
 *
 * Issue #762 — when `task_mode_offered` is true (the `TARII_TASK_MODE`
 * env gate is on), a "Kind" step is prepended: the lead picks
 * Scheduling or Map before naming the team. The gate being off leaves
 * the flow byte-identical to today (no step added, `team_kind`
 * defaults to `'scheduling'` on the POST body). A tasks team skips the
 * scheduler-specific configure step and routes to its map home.
 *
 * Scheduling steps: Team → Invite → Configure
 * With gate on:     Kind → Team → Invite → Configure
 * Tasks fast-exit:  Kind → Team → Invite → (routed to /teams/:id/map)
 *
 * The wizard is self-contained — no AppShell. The progress strip is
 * the only chrome.
 */
import { useQueryClient } from "@tanstack/react-query";
import { ArrowRight, Calendar, Check, Map as MapIcon, Settings } from "lucide-react";
import { type FormEvent, useState } from "react";
import { useNavigate } from "react-router-dom";

import { ApiError, apiFetch } from "../api/client";
import type { paths } from "../api/schema.gen";
// Brief 36 / issue 017 — the "Start with a template" affordance was
// removed from Step 3 because demo scenarios are `/demo`-only at v0.1.
// Newly-created teams (the only kind a brand-new account reaches this
// wizard with) carry `is_demo=false`, so the apply-template POST would
// 403 anyway — hiding the entry-point removes the dead-end CTA. The
// `<LoadTemplateDialog/>` import + the `ScenarioTemplate` type +
// the template-loaded callback paths come back when the v0.2 curated
// template library lands (chunk 5 of issue 009 / T6 — deferred per
// Brief 36 scope-cut).
import { Logo } from "../components/Logo";
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Input,
  Label,
  PageLead,
  Textarea,
} from "../components/ui";
import { useAuth } from "../lib/AuthContext";
import { cn } from "../lib/cn";
import { teamInviteQuery } from "../lib/productInvite";
import { productByKey } from "../lib/products";
import { clearSignupProduct, peekSignupProduct } from "../lib/signupProduct";
import { TEAM_APPS, type TeamKind, resolveTeamHomeRoute } from "../lib/teamApps";
import { useClaimStoredInvite } from "../lib/useClaimStoredInvite";
import { workspacesKey } from "../lib/workspacesKey";

type Team = paths["/api/teams"]["post"]["responses"]["201"]["content"]["application/json"];

const FLASH_KEY = "roster.flash";

// ─── Step definitions ──────────────────────────────────────────────────────
//
// When `task_mode_offered` is false we use the 3-step scheduling flow;
// when true the kind-picker step is prepended. The step list is derived
// at render time so ProgressStrip always reflects exactly the steps the
// current visitor will walk through.

const SCHEDULING_STEPS = [
  { key: "team", label: "Team" },
  { key: "invites", label: "Invite" },
  { key: "configure", label: "Configure" },
] as const;

const TASK_MODE_STEPS = [
  { key: "kind", label: "App" },
  { key: "team", label: "Team" },
  { key: "invites", label: "Invite" },
  { key: "configure", label: "Configure" },
] as const;

type SchedulingStepKey = (typeof SCHEDULING_STEPS)[number]["key"];
type TaskModeStepKey = (typeof TASK_MODE_STEPS)[number]["key"];
type StepKey = SchedulingStepKey | TaskModeStepKey;

export function OnboardingPage() {
  // #2652 — an invite code stored on `/start` is claimed for this account,
  // and the account is read again, before the kind picker reads the offers.
  const claiming = useClaimStoredInvite();
  if (claiming) return <div aria-busy="true" data-testid="onboarding-claiming" />;
  return <OnboardingWizard />;
}

function OnboardingWizard() {
  const { authHeader, account, accountId, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Issue #762 — gate the kind picker off `task_mode_offered`. When the
  // field is missing or false (the default), the scheduling-only 3-step
  // flow is preserved byte-for-byte. When true, the kind picker is
  // prepended as the first step and the team kind is carried forward.
  //
  // `task_mode_offered` is read from the async account snapshot — it is
  // false until the `/api/account/me` response lands. We derive the
  // current step from `step` + `kindChosen` rather than baking it into
  // the useState initializer (which would always see null account on
  // first render and default to the gate-off path).
  const taskModeOffered = account?.task_mode_offered ?? false;

  // #2630: the product answered on `/start` before sign-up. When the
  // account may start it, the preset decides the team kind (the API sets
  // it and the components), so the kind picker below is skipped. With no
  // stash, or a product this account is not offered, the flow is today's.
  const [stashedProduct] = useState(() => peekSignupProduct());
  const offeredProducts = account?.products_offered;
  const preset =
    stashedProduct && offeredProducts?.includes(stashedProduct)
      ? productByKey(stashedProduct)
      : null;

  // `kindChosen` tracks whether the lead has completed the kind-picker
  // step. False → show the picker (if gate is on); true → show the rest
  // of the wizard. Gate-off skips this entirely.
  const [kindChosen, setKindChosen] = useState(false);
  const [step, setStep] = useState<StepKey>("team");
  const [team, setTeam] = useState<Team | null>(null);
  // Holds the chosen team kind across steps. Defaults to 'scheduling' so
  // the existing POST body is unchanged when the gate is off.
  const [pickedKind, setTeamKind] = useState<TeamKind>("scheduling");
  const teamKind: TeamKind = preset ? preset.teamKind : pickedKind;

  // Determine which step list and which logical current step to pass to
  // ProgressStrip. When the gate is on and kind hasn't been chosen yet,
  // the user is on the "kind" step of the 4-step list.
  const showingKindPicker = taskModeOffered && !kindChosen && !preset;
  const steps = taskModeOffered && !preset ? TASK_MODE_STEPS : SCHEDULING_STEPS;
  const progressStep: StepKey = showingKindPicker ? "kind" : step;

  const onSkip = () => navigate("/", { replace: true });

  // Stash the welcome flash on whichever exit the lead picks. Both
  // routes (`/setup/sessions` and `/teams/:id`) consume the same key,
  // so we only need to write it once at the hand-off.
  const writeFlash = (teamName: string) => {
    sessionStorage.setItem(FLASH_KEY, JSON.stringify({ kind: "onboarding_done", teamName }));
  };

  const onConfigure = (teamId: string) => {
    writeFlash(team?.name ?? "your team");
    // Tasks teams have no scheduler-specific setup — route to their map
    // home instead. Scheduling teams go to the Setup tree as today.
    if (teamKind === "tasks") {
      navigate(resolveTeamHomeRoute("tasks", teamId), { replace: true });
    } else {
      navigate(`/teams/${teamId}/setup/sessions`, { replace: true });
    }
  };

  const onSkipToTeam = (teamId: string) => {
    writeFlash(team?.name ?? "your team");
    // For tasks teams: route to the map home. For scheduling teams: the
    // existing /teams/:id. resolveTeamHomeRoute handles both kinds.
    navigate(resolveTeamHomeRoute(teamKind, teamId), { replace: true });
  };

  return (
    <main className="mx-auto flex min-h-screen max-w-2xl flex-col gap-8 px-4 py-12 sm:px-6">
      <header className="flex items-center justify-between">
        <Logo />
        <Button variant="ghost" size="sm" className="h-11 px-4" onClick={onSkip}>
          Skip for now
        </Button>
      </header>

      <PageLead
        title="Set up your team"
        subtitle="A few steps to get going. We'll save your progress as you go."
        state={<ProgressStrip steps={steps} current={progressStep} />}
      />

      {/* Issue #762 — kind picker (B1.1). Only visible when gate is on
          AND the lead has not yet chosen a kind. Because `taskModeOffered`
          derives from the async account snapshot, `showingKindPicker`
          flips from false→true only after /api/account/me resolves with
          task_mode_offered: true. The scheduling flow (gate off) never
          renders this element. */}
      {showingKindPicker ? (
        <KindStep
          onChosen={(kind) => {
            setTeamKind(kind);
            setKindChosen(true);
          }}
        />
      ) : null}

      {/* #2630: hold the form until the account (and so the offer list) has
          loaded, so a stored product is never dropped by a quick submit. */}
      {!showingKindPicker && step === "team" && stashedProduct && !account && authLoading ? (
        <p className="text-fg-subtle text-sm">Loading your account.</p>
      ) : null}
      {!showingKindPicker && step === "team" && !(stashedProduct && !account && authLoading) ? (
        <TeamStep
          authHeader={authHeader}
          teamKind={teamKind}
          productPreset={preset?.key ?? null}
          onCreated={(t) => {
            if (preset) clearSignupProduct();
            setTeam(t);
            setStep("invites");
            // Issue #861 — the team list is cached in two places: the
            // always-mounted <WorkspaceSwitcher/> (`useWorkspaces`,
            // staleTime 60s) and HomePage's `["teams"]` query. Nothing
            // in the create funnel told them a team was just created, so
            // a freshly-created team stayed invisible in the switcher
            // for up to 60s. It read as Map-specific because the tasks
            // fast-exit routes straight to /teams/:id/map and never
            // re-mounts HomePage. Invalidate both so the new team shows
            // immediately, kind-agnostically.
            queryClient.invalidateQueries({ queryKey: workspacesKey(accountId) });
            queryClient.invalidateQueries({ queryKey: ["teams", accountId] });
          }}
        />
      ) : null}
      {!showingKindPicker && step === "invites" && team ? (
        <InvitesStep team={team} authHeader={authHeader} onNext={() => setStep("configure")} />
      ) : null}
      {!showingKindPicker && step === "configure" && team ? (
        <ConfigureStep
          team={team}
          teamKind={teamKind}
          onConfigure={() => onConfigure(team.id)}
          onSkip={() => onSkipToTeam(team.id)}
        />
      ) : null}
    </main>
  );
}

// ─── ProgressStrip ──────────────────────────────────────────────────────────

function ProgressStrip({
  steps,
  current,
}: {
  steps: readonly { key: string; label: string }[];
  current: StepKey;
}) {
  const idx = steps.findIndex((s) => s.key === current);
  return (
    <ol className="flex items-center gap-3 text-xs uppercase tracking-[0.12em] text-fg-faint">
      {steps.map((s, i) => {
        const done = i < idx;
        const active = i === idx;
        return (
          <li key={s.key} className="flex items-center gap-2">
            <span
              className={cn(
                "flex h-6 w-6 items-center justify-center rounded-full border text-xs",
                done
                  ? "border-success bg-success-soft text-success"
                  : active
                    ? "border-accent bg-accent-soft text-accent-fg"
                    : "border-border bg-bg-elevated text-fg-faint",
              )}
            >
              {done ? <Check className="h-3 w-3" aria-hidden="true" /> : i + 1}
            </span>
            <span className={active ? "text-fg" : undefined}>{s.label}</span>
            {i < steps.length - 1 ? (
              <ArrowRight className="h-3 w-3 text-fg-faint" aria-hidden="true" />
            ) : null}
          </li>
        );
      })}
    </ol>
  );
}

// ─── Step 0 (gated) — kind picker (B1.1) ───────────────────────────────────
//
// Issue #762. Shown only when `task_mode_offered` is true. Two clear
// choices: Scheduling (the existing roster/shift app) and Map (the
// task-tree app). Scheduling is the focal element — it is the primary
// path and is visually elevated. Map is clearly available but secondary,
// per `feedback_ui_section_focal_component.md` (one focal element per
// section; avoid uniform grids where everything competes).
//
// Labels + descriptions pulled from TEAM_APPS so the registry stays the
// single source of truth for per-kind identity.

function KindStep({ onChosen }: { onChosen: (kind: TeamKind) => void }) {
  const [selected, setSelected] = useState<TeamKind | null>(null);

  const schedulingApp = TEAM_APPS.scheduling;
  const tasksApp = TEAM_APPS.tasks;

  return (
    <Card data-testid="kind-step">
      <CardHeader>
        <CardTitle>What will this team do?</CardTitle>
        <p className="text-sm text-fg-subtle">
          Pick how this team works — you can't change this later.
        </p>
      </CardHeader>
      <CardContent className="flex flex-col gap-6">
        {/* Focal card — Scheduling (primary path) */}
        <button
          type="button"
          data-testid="kind-option-scheduling"
          onClick={() => setSelected("scheduling")}
          className={cn(
            "flex items-start gap-4 rounded-lg border p-4 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent",
            selected === "scheduling"
              ? "border-accent bg-accent-soft"
              : "border-border bg-bg-subtle hover:border-accent/60 hover:bg-bg-subtle/80",
          )}
          aria-pressed={selected === "scheduling"}
        >
          <span
            className={cn(
              "mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-md",
              selected === "scheduling"
                ? "bg-accent text-accent-contrast"
                : "bg-bg-elevated text-fg",
            )}
          >
            <Calendar className="h-5 w-5" aria-hidden="true" />
          </span>
          <div className="flex flex-col gap-0.5">
            <span className="font-semibold text-fg">{schedulingApp.label}</span>
            <span className="text-sm text-fg-subtle">
              Rosters and shifts — plan who's on, when, and balance the load fairly.
            </span>
          </div>
          {selected === "scheduling" ? (
            <Check className="ml-auto mt-1 h-4 w-4 shrink-0 text-accent" aria-hidden="true" />
          ) : null}
        </button>

        {/* Secondary card — Map (tasks app) */}
        <button
          type="button"
          data-testid="kind-option-tasks"
          onClick={() => setSelected("tasks")}
          className={cn(
            "flex items-start gap-4 rounded-lg border p-4 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent",
            selected === "tasks"
              ? "border-accent bg-accent-soft"
              : "border-border hover:border-accent/60",
          )}
          aria-pressed={selected === "tasks"}
        >
          <span
            className={cn(
              "mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-md",
              selected === "tasks" ? "bg-accent text-accent-contrast" : "bg-bg-elevated text-fg",
            )}
          >
            <MapIcon className="h-5 w-5" aria-hidden="true" />
          </span>
          <div className="flex flex-col gap-0.5">
            <span className="font-semibold text-fg">{tasksApp.label}</span>
            <span className="text-sm text-fg-subtle">
              A tree of the work — what's in scope, who's on it, where it stands.
            </span>
          </div>
          {selected === "tasks" ? (
            <Check className="ml-auto mt-1 h-4 w-4 shrink-0 text-accent" aria-hidden="true" />
          ) : null}
        </button>

        <div className="flex justify-end">
          <Button
            type="button"
            className="h-11"
            data-testid="kind-step-continue"
            disabled={selected === null}
            onClick={() => {
              if (selected !== null) onChosen(selected);
            }}
          >
            Continue
            <ArrowRight className="h-4 w-4" aria-hidden="true" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// ─── Step 1 — create team ─────────────────────────────────────────────

function TeamStep({
  authHeader,
  teamKind,
  productPreset,
  onCreated,
}: {
  authHeader: () => Promise<Record<string, string>>;
  teamKind: TeamKind;
  productPreset: string | null;
  onCreated: (team: Team) => void;
}) {
  const [name, setName] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);
    if (!name.trim() || !displayName.trim()) {
      setError("Both fields are required.");
      return;
    }
    setBusy(true);
    try {
      const headers = await authHeader();
      const created = await apiFetch<Team>(`/api/teams${teamInviteQuery()}`, {
        method: "POST",
        headers,
        body: JSON.stringify({
          name: name.trim(),
          display_name_in_team: displayName.trim(),
          // Issue #762 — carry the chosen kind forward. Defaults to
          // 'scheduling' so the gate-off path is byte-identical to today.
          team_kind: teamKind,
          // #2630: null keeps today's body. A key lets the API set the
          // kind and the starting components from the preset.
          ...(productPreset ? { product_preset: productPreset } : {}),
        }),
      });
      onCreated(created);
    } catch (err) {
      setError(err instanceof ApiError ? err.detail : (err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Create your team</CardTitle>
        <p className="text-sm text-fg-subtle">
          Use the team's working name — your members will see this in the app.
        </p>
      </CardHeader>
      <CardContent>
        <form onSubmit={onSubmit} className="flex flex-col gap-4">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="team-name">Team name</Label>
            <Input
              id="team-name"
              className="h-11"
              autoFocus
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Cardiology Junior Doctors"
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="display-name">Your display name on the team</Label>
            <Input
              id="display-name"
              className="h-11"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="e.g. Lee (Lead)"
            />
          </div>
          {error ? (
            <p role="alert" className="text-sm text-danger">
              {error}
            </p>
          ) : null}
          <div className="flex justify-end">
            <Button type="submit" className="h-11" disabled={busy}>
              {busy ? "Creating…" : "Create team"}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}

// ─── Step 2 — invite members ───────────────────────────────────��─────

function InvitesStep({
  team,
  authHeader,
  onNext,
}: {
  team: Team;
  authHeader: () => Promise<Record<string, string>>;
  onNext: () => void;
}) {
  const [emails, setEmails] = useState("");
  const [busy, setBusy] = useState(false);
  const [results, setResults] = useState<{ email: string; ok: boolean; error?: string }[]>([]);

  const send = async () => {
    const lines = emails
      .split(/[,\n]/)
      .map((s) => s.trim())
      .filter(Boolean);
    if (lines.length === 0) {
      onNext();
      return;
    }
    setBusy(true);
    const out: typeof results = [];
    const headers = await authHeader();
    for (const email of lines) {
      try {
        await apiFetch(`/api/teams/${team.id}/invites`, {
          method: "POST",
          headers,
          body: JSON.stringify({ email, role: "member" }),
        });
        out.push({ email, ok: true });
      } catch (err) {
        out.push({
          email,
          ok: false,
          error: err instanceof ApiError ? err.detail : (err as Error).message,
        });
      }
    }
    setResults(out);
    setBusy(false);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Invite your team</CardTitle>
        <p className="text-sm text-fg-subtle">
          One email per line, or comma-separated. Each invitee gets an email with a sign-in link.
          You can skip and invite later from the team page.
        </p>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="invite-emails">Email addresses</Label>
          <Textarea
            id="invite-emails"
            value={emails}
            onChange={(e) => setEmails(e.target.value)}
            placeholder={"alex@example.com\nblake@example.com"}
            rows={5}
          />
        </div>
        {results.length > 0 ? (
          <ul className="space-y-1 text-xs">
            {results.map((r) => (
              <li key={r.email} className="flex items-center gap-2">
                <Badge tone={r.ok ? "success" : "danger"}>{r.ok ? "sent" : "failed"}</Badge>
                <span className="font-mono text-fg-muted">{r.email}</span>
                {r.error ? <span className="text-danger">{r.error}</span> : null}
              </li>
            ))}
          </ul>
        ) : null}
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onNext} disabled={busy} data-testid="invites-skip">
            Skip
          </Button>
          <Button onClick={send} disabled={busy}>
            {busy ? "Sending…" : results.length > 0 ? "Continue" : "Send invites"}
          </Button>
        </div>
        {results.length > 0 && !busy ? (
          <div className="flex justify-end">
            <Button variant="secondary" onClick={onNext}>
              Next: configure your team
            </Button>
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}

// ─── Step 3 — configure your team ────────────────────────────────────
//
// Replaces the old `CycleStep` per W1-I G-EMPTY2. Opening a cycle
// before the team has any stations / on-call types / tiers produces a
// useless first roster, so we hand the lead off to the Setup tree
// instead. The recommended ordering matches `/teams/:id/setup/sessions`
// → stations → oncall → rules. The lead can still bail to TeamPage —
// the empty-config callout (G-EMPTY1) will catch them there.
//
// Issue #762 — for tasks teams (Map), the scheduler-specific setup
// checklist is not surfaced. The primary CTA routes to the Map home;
// the secondary "I'll do this later" also routes there.

function ConfigureStep({
  team,
  teamKind,
  onConfigure,
  onSkip,
}: {
  team: Team;
  teamKind: TeamKind;
  onConfigure: () => void;
  onSkip: () => void;
}) {
  // Tasks teams skip the scheduler configure surface. Route to Map home.
  if (teamKind === "tasks") {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Your Map is ready</CardTitle>
          <p className="text-sm text-fg-subtle">
            <strong>{team.name}</strong> has been created. Head to your Map to start building out
            the work tree — add nodes, assign people, and track progress as it unfolds.
          </p>
        </CardHeader>
        <CardContent className="flex flex-col gap-5">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-end">
            <Button type="button" onClick={onConfigure} data-testid="onboarding-configure-cta">
              <MapIcon className="h-4 w-4" aria-hidden="true" />
              Go to my Map
              <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Configure your team</CardTitle>
        <p className="text-sm text-fg-subtle">
          Before your first solve, set up <strong>{team.name}</strong>'s sessions, stations, on-call
          types, and rules. The solver needs this shape to build a useful roster — without it, the
          first solve would come back empty.
        </p>
      </CardHeader>
      <CardContent className="flex flex-col gap-5">
        <ol className="flex flex-col gap-2 rounded-md border border-border bg-bg-subtle/50 p-4 text-sm text-fg-muted">
          <li className="flex items-center gap-2">
            <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-accent-soft text-[11px] font-semibold text-accent-fg">
              1
            </span>
            <span>
              <span className="font-medium text-fg">Sessions &amp; tiers</span> — wall-clock blocks
              + tier labels.
            </span>
          </li>
          <li className="flex items-center gap-2">
            <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-bg-elevated text-[11px] font-semibold text-fg-muted">
              2
            </span>
            <span>
              <span className="font-medium text-fg">Stations</span> — the workstations or roles your
              team rotates through.
            </span>
          </li>
          <li className="flex items-center gap-2">
            <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-bg-elevated text-[11px] font-semibold text-fg-muted">
              3
            </span>
            <span>
              <span className="font-medium text-fg">On-call types</span> — overnight + weekend shift
              families.
            </span>
          </li>
          <li className="flex items-center gap-2">
            <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-bg-elevated text-[11px] font-semibold text-fg-muted">
              4
            </span>
            <span>
              <span className="font-medium text-fg">Rules &amp; weights</span> — hard constraints +
              balance knobs.
            </span>
          </li>
        </ol>
        {/* Brief 36 / issue 017 — the "Start with a template" CTA was
            removed from this step. Demo scenarios are `/demo`-only at
            v0.1; a curated, generic template library replaces it once
            v0.2 lands (deferred per Brief 36 scope-cut). */}
        <p className="text-xs text-fg-subtle">Most teams finish in about five minutes.</p>
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-end">
          <Button
            type="button"
            variant="ghost"
            onClick={onSkip}
            data-testid="onboarding-configure-skip"
          >
            I'll do this later
          </Button>
          <Button type="button" onClick={onConfigure} data-testid="onboarding-configure-cta">
            <Settings className="h-4 w-4" aria-hidden="true" />
            Take me to configure my team
            <ArrowRight className="h-4 w-4" aria-hidden="true" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
